import { EXPLAINED_ERROR_CODES, type ApiErrorBody, type ApiErrorCode } from '@shared/api';

/**
 * The one place the browser talks to the hosted API.
 *
 * Everything in `src/cloud/` goes through this, which is what makes three cross-cutting concerns
 * single-sited: credentials are always included (the session lives in an httpOnly cookie, so a
 * `fetch` that forgets `credentials` silently behaves as an anonymous caller), the CSRF header is
 * always present, and a non-2xx response always becomes a typed {@link CloudError} rather than a
 * rejected promise carrying an HTML error page.
 *
 * `code` on the error is the part the UI actually branches on: a quota wall has to offer the
 * "add your own key" panel, and an expired session has to re-prompt sign-in rather than show a
 * failure toast. A bare status code cannot tell those apart from any other 429 or 401.
 */

/** Sent on every mutating request; the server refuses state changes without it. See `auth/guard`. */
const CSRF_HEADER = 'x-glade-app';

export class CloudError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly retryAfter?: number,
  ) {
    super(message);
    this.name = 'CloudError';
  }

  /** Whether the caller should be sent back through sign-in. */
  get needsSignIn(): boolean {
    return this.code === 'unauthenticated';
  }

  /** Whether retrying the same request later could succeed. */
  get retryable(): boolean {
    return this.status >= 500 || this.code === 'rate_limited';
  }

  /**
   * Whether {@link message} is already the sentence to put in front of the user.
   *
   * A caller that falls through to its own per-status wording replaces a specific answer with a
   * worse one: "403" becomes "you do not have access" for a library that is simply full, and a
   * 413 becomes "see the console" for a document whose size the server just measured and named.
   * See `EXPLAINED_ERROR_CODES`.
   */
  get explained(): boolean {
    return EXPLAINED_ERROR_CODES.includes(this.code);
  }
}

export interface ApiClientOptions {
  /** Defaults to the current origin. Set when the SPA is hosted apart from the API. */
  baseUrl?: string;
  /** Injected for tests. */
  fetchImpl?: typeof fetch;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch | undefined;

  constructor(options: ApiClientOptions = {}) {
    this.baseUrl = (options.baseUrl ?? '').replace(/\/$/, '');
    this.fetchImpl = options.fetchImpl;
  }

  /** Absolute URL for a path — used for `<img src>` on stored photos, which bypasses fetch. */
  url(path: string): string {
    return `${this.baseUrl}${path}`;
  }

  async getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
    return this.request<T>('GET', path, undefined, signal);
  }

  async putJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>('PUT', path, body, signal);
  }

  async postJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>('POST', path, body, signal);
  }

  async patchJson<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
    return this.request<T>('PATCH', path, body, signal);
  }

  async delete(path: string, signal?: AbortSignal): Promise<void> {
    await this.request<unknown>('DELETE', path, undefined, signal);
  }

  /** Multipart upload, for captured images. */
  async postForm<T>(path: string, form: FormData, signal?: AbortSignal): Promise<T> {
    const response = await this.send(path, {
      method: 'POST',
      // No Content-Type: the browser must set it, including the multipart boundary.
      headers: { [CSRF_HEADER]: '1' },
      body: form,
      ...(signal ? { signal } : {}),
    });
    return this.parse<T>(response);
  }

  /** Fetch a stored image as a Blob, carrying the session cookie. */
  async getBlob(path: string, signal?: AbortSignal): Promise<Blob> {
    const response = await this.send(path, {
      method: 'GET',
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) throw await this.toError(response);
    return response.blob();
  }

  private async request<T>(
    method: string,
    path: string,
    body: unknown,
    signal?: AbortSignal,
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (method !== 'GET' && method !== 'HEAD') headers[CSRF_HEADER] = '1';
    if (body !== undefined) headers['Content-Type'] = 'application/json';

    const response = await this.send(path, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
    return this.parse<T>(response);
  }

  private async send(path: string, init: RequestInit): Promise<Response> {
    const doFetch = this.fetchImpl ?? globalThis.fetch;
    if (typeof doFetch !== 'function') {
      throw new CloudError(0, 'internal', 'No fetch implementation is available.');
    }
    try {
      return await doFetch(this.url(path), { ...init, credentials: 'include' });
    } catch (error) {
      // An abort is the caller's own doing and must propagate as-is so `AbortError` checks work.
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new CloudError(
        0,
        'upstream_error',
        'Could not reach the server. Check your connection.',
      );
    }
  }

  private async parse<T>(response: Response): Promise<T> {
    if (!response.ok) throw await this.toError(response);
    if (response.status === 204) return undefined as T;
    const text = await response.text();
    if (text.length === 0) return undefined as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new CloudError(response.status, 'internal', 'The server sent a response we could not read.');
    }
  }

  private async toError(response: Response): Promise<CloudError> {
    let body: Partial<ApiErrorBody> = {};
    try {
      body = (await response.json()) as Partial<ApiErrorBody>;
    } catch {
      // Non-JSON error — a proxy's 502 page, most likely. Fall through to the status text.
    }

    const detail = body.error;
    return new CloudError(
      response.status,
      detail?.code ?? fallbackCode(response.status),
      detail?.message ?? `Request failed (${String(response.status)} ${response.statusText}).`,
      detail?.retryAfter,
    );
  }
}

function fallbackCode(status: number): ApiErrorCode {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 413) return 'payload_too_large';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'internal';
  return 'invalid_request';
}
